import {
  Box,
  Button,
  IconButton,
  Link,
  TextField,
  Typography,
  Chip,
  alpha,
  Paper,
  useTheme,
} from '@mui/material';
import { genericFormProps } from '../signup.types';
import { useCallback, useContext, useState } from 'react';
import YouTubeIcon from '@mui/icons-material/YouTube';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { TierContext } from '../../../context/TierContext';
import DeleteIcon from '../../../svg/DeleteIcon3';

const YoutubeLinksForm: React.FC<genericFormProps> = (props) => {
  const {
    values,
    handleBlur,
    setFieldValue,
  } = props;

  const theme = useTheme();
  const [youtubeLink, setYoutubeLink] = useState<string>('');
  const [ytErrors, setYTErrors] = useState<string>(null);
  const { checkPermission } = useContext(TierContext);
  const validYoutubeLinks = (values?.youtube_links || []).filter(
    (doc) => doc && doc.url
  );

  const handleAddLink = () => {
    if (youtubeLink) {
      // validate
      setYTErrors(null);
      const validYTLink =
        /^(https:\/\/)?(www\.)?(youtube\.com\/watch\?v=|youtube\.com\/embed\/)[\w-]{11}/;
      if (!validYTLink.test(youtubeLink)) {
        setYTErrors(
          'Invalid YouTube link. Must start with https://youtube.com/watch?v={video_id} or https://youtube.com/embed/{video_id}'
        );
      } else {
        // add to google drive folder list
        let copy = [...values.youtube_links];
        copy.push({
          url: youtubeLink,
        });
        setFieldValue('youtube_links', copy);
        setYoutubeLink('');
      }
    }
  };

  const renderYTListItem = useCallback(
    (document: any, index: number, onDeleteHandler: () => void) => {
      const isIndexed = document?.hasOwnProperty('is_indexed') ? document.is_indexed : null;
      const isNewLink = !document?.hasOwnProperty('is_indexed');

      return (
        <Box
          key={`youtubelink-${index}`}
          display='flex'
          flexDirection='column'
          borderBottom={1}
          borderColor='divider'
          px={2.5}
          py={1.5}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Box>
                <Link
                  underline='hover'
                  href={`${document.url}`}
                  rel='noreferrer'
                  target='_blank'
                  sx={{
                    color: isIndexed === false ? 'error.main' : 'text.primary',
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      wordBreak: 'break-word',
                    }}
                  >
                    {document.url}
                  </Typography>
                </Link>

                {isIndexed === false && (
                  <Box display='flex' alignItems='center' gap={0.5} mt={0.5}>
                    <ErrorIcon sx={{ fontSize: 14, color: 'error.main' }} />
                    <Typography variant='caption' color='error.main'>
                      Failed to process
                    </Typography>
                  </Box>
                )}

                {isIndexed === true && (
                  <Box display='flex' alignItems='center' gap={0.5} mt={0.5}>
                    <CheckCircleIcon sx={{ fontSize: 14, color: 'success.main' }} />
                    <Typography variant='caption' color='success.main'>
                      Processed successfully
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            <IconButton
              id={`button-delete-${document.url}-${index}`}
              size='small'
              sx={{
                width: '30px',
                height: '30px',
              }}
              onClick={onDeleteHandler}
            >
              <DeleteIcon stroke={theme.palette["nav"].iconactive} />
            </IconButton>
          </Box>
        </Box>
      );
    },
    [theme]
  );

  const hasYoutubeLinks = !!validYoutubeLinks.length;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      {/* Page Title */}
      <Box>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 600,
            color: 'text.primary',
            mb: 0.5,
          }}
        >
          YouTube Links
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
          }}
        >
          Train your AI persona with your YouTube videos
        </Typography>
           <Typography
              sx={{
                  color: 'primary.secondary',
                  fontSize: 16,
                  fontFamily: '"Merriweather Sans", sans-serif',
                  fontWeight: 600,
                  lineHeight: '20.8px',
                  letterSpacing: '0.15px',
                  mb: 2,
                  mt: 2,
                }}
            >
               *Optional
            </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          p: 2,
          borderRadius: 4,
          border: `1px solid ${theme.palette.divider}`,
          bgcolor: theme.palette.background["container-2"]
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            mb: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'start',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <YouTubeIcon sx={{ color: '#FF0000' }} />
            <Typography variant='h7'>Add YouTube Links</Typography>
          </Box>
          <Typography variant='body2'>
            Add links to your YouTube videos to help train your AI persona with your video content and speaking style.
          </Typography>
        </Box>

        {/* YouTube Link Input */}
        <TextField
          type='string'
          fullWidth
          name='youtube_links'
          margin='none'
          placeholder='https://youtube.com/watch?v=...'
          value={youtubeLink}
          onBlur={handleBlur}
          onChange={(e) => {
            if (checkPermission("train:youtube")) {
              setYoutubeLink(e.target.value)
            }
          }}
          error={Boolean(ytErrors)}
          helperText={ytErrors}
          autoComplete='off'
          onClick={() => {
            checkPermission("train:youtube")
          }}
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
            },
          }}
        />

        <Button
          variant='contained'
          size='large'
          fullWidth
          sx={{
            borderRadius: 2,
            py: 1.5,
            fontWeight: 600,
            boxShadow: theme.shadows[4],
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              boxShadow: theme.shadows[8],
              transform: 'translateY(-2px)',
            },
          }}
          onClick={() => {
            if (checkPermission('train:youtube')) {
              handleAddLink();
            }
          }}
          disabled={!youtubeLink}
        >
          Add YouTube Link
        </Button>
      </Paper>

      {/* YouTube Links List Section */}
      {hasYoutubeLinks && (
        <Box mt={1}>
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderRadius: 3,
              border: `1px solid ${theme.palette.divider}`,
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: 'space-between',
              bgcolor: theme.palette.background["container-2"]
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: 1,
              }}
            >
              <YouTubeIcon sx={{ color: '#FF0000' }} />
              <Typography variant="h7">My YouTube Links</Typography>
            </Box>
            <Chip
              label={`${validYoutubeLinks.length} ${validYoutubeLinks.length === 1 ? 'video' : 'videos'}`}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                color: 'primary.main',
              }}
            />
          </Paper>

          <Box>
            {validYoutubeLinks.map((document, index) =>
              renderYTListItem(document, index, () => {
                const copy = [...values.youtube_links];
                const originalIndex = values.youtube_links.findIndex(
                  (item) => item.url === document.url
                );
                if (originalIndex > -1) {
                  copy.splice(originalIndex, 1);
                  setFieldValue('youtube_links', copy);
                }
              })
            )}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default YoutubeLinksForm;
